import type React from "react"
import { useState, useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Stack,
  FormControlLabel,
  Switch,
  InputAdornment,
  IconButton,
  Typography,
  Box,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material"
import { Visibility, VisibilityOff, Person, Badge } from "@mui/icons-material"
import type { User, UserFormData } from "../../utils/userAPI"
import { GetTeamLeads } from "../../utils/APIRequest";

interface UserFormProps {
  open: boolean
  onClose: () => void
  onSubmit: (userData: UserFormData) => void
  user?: User | null
  mode: "add" | "edit"
  teamLeads?: User[]
}

const UserForm: React.FC<UserFormProps> = ({ open, onClose, onSubmit, user, mode, teamLeads }) => {
  const [leadOptions, setLeadOptions] = useState<User[]>(teamLeads || []);
  const designationOptions = [
    { value: "developer", label: "Software Developer" },
    { value: "senior_developer", label: "Senior Software Developer" },
    { value: "asso_team_lead", label: "Associate Team Lead" },
    { value: "team_lead", label: "Team Lead" },
    { value: "ba", label: "Business Analyst" },
    { value: "qa", label: "Quality Analyst" },
    { value: "project_manager", label: "Project Manager" },
    { value: "hr", label: "HR" },
  ];
  const [formData, setFormData] = useState<UserFormData>({
    username: "",
    emp_name: "",
    password: "",
    isAdmin: false,
    teamLead: "",
    designation: "",
  })
  const [errors, setErrors] = useState<Partial<UserFormData>>({})
  const [submitError, setSubmitError] = useState<string>("")
  const [showPassword, setShowPassword] = useState(false)

  // Reset form when dialog opens/closes or user changes
  useEffect(() => {
    // Fetch team leads from API when dialog opens
    if (open) {
      GetTeamLeads().then((leads) => setLeadOptions(leads));
    }
    if (open) {
      if (mode === "edit" && user) {
        setFormData({
          username: user.username,
          emp_name: user.emp_name,
          password: user.password || "",
          isAdmin: user.isAdmin,
          designation: user.designation || "",
          teamLead: user.teamLead || "",
        })
      } else {
        setFormData({
          username: "",
          emp_name: "",
          password: "",
          isAdmin: false,
          teamLead: "",
          designation: "",
        })
      }
      setErrors({})
      setSubmitError("")
      setShowPassword(false)
    }
  }, [open, mode, user])

  const validateForm = (): boolean => {
    const newErrors: Partial<UserFormData> = {}

    if (!formData.username.trim()) {
      newErrors.username = "Username is required"
    } else if (formData.username.length < 3) {
      newErrors.username = "Username must be at least 3 characters"
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = "Username can only contain letters, numbers, and underscores"
    }

    if (!formData.emp_name.trim()) {
      newErrors.emp_name = "Employee name is required"
    } else if (formData.emp_name.length < 2) {
      newErrors.emp_name = "Employee name must be at least 2 characters"
    }

    if (mode === "add") {
      if (!formData.password.trim()) {
        newErrors.password = "Password is required"
      } else if (formData.password.length < 4) {
        newErrors.password = "Password must be at least 4 characters"
      }
    } else if (mode === "edit" && formData.password) {
      if (formData.password.length < 4) {
        newErrors.password = "Password must be at least 4 characters"
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      onSubmit(formData)
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "An error occurred")
    }
  }

  const handleInputChange = (field: keyof UserFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field as keyof Partial<UserFormData>]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
    setSubmitError("")
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {mode === "add" ? <Person /> : <Badge />}
          <Typography variant="h6">{mode === "add" ? "Add New User" : "Edit User"}</Typography>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Username"
              value={formData.username}
              onChange={(e) => handleInputChange("username", e.target.value)}
              error={!!errors.username}
              helperText={
                errors.username ||
                (mode === "edit" ? "Username cannot be changed" : "Use letters, numbers, and underscores only")
              }
              required
              disabled={mode === "edit"}
              placeholder="e.g., john_doe or 1001"
            />

            <TextField
              fullWidth
              label="Employee Name"
              value={formData.emp_name}
              onChange={(e) => handleInputChange("emp_name", e.target.value)}
              error={!!errors.emp_name}
              helperText={errors.emp_name}
              required
              placeholder="e.g., John Doe"
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={(e) => handleInputChange("password", e.target.value)}
              error={!!errors.password}
              helperText={errors.password || "Minimum 4 characters"}
              required={mode === "add"}
              placeholder="Enter a secure password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControl fullWidth>
              <InputLabel id="designation-label">Designation</InputLabel>
              <Select
                labelId="designation-label"
                label="Designation"
                value={formData.designation || ""}
                onChange={(e) => handleInputChange("designation", e.target.value)}
              >
                <MenuItem value="">Select designation</MenuItem>
                {designationOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="team-lead-label">Team Lead</InputLabel>
              <Select
                labelId="team-lead-label"
                label="Team Lead"
                value={formData.teamLead || ""}
                onChange={(e) => handleInputChange("teamLead", e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {(leadOptions || [])
                  .filter((lead: User) => lead.designation === "team_lead" || lead.designation === "asso_team_lead")
                  .map((lead: User) => (
                    <MenuItem key={lead.username} value={lead.username}>{lead.emp_name}</MenuItem>
                  ))}
              </Select>
            </FormControl>

            <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isAdmin}
                    onChange={(e) => handleInputChange("isAdmin", e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">Admin User</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formData.isAdmin
                        ? "This user will have administrative privileges"
                        : "This user will have standard employee access"}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={onClose} color="primary">
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary" size="large">
            {mode === "add" ? "Add User" : "Update User"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default UserForm
